// Throwaway prototype: chosen combo-builder shape with a permanent left combos panel.
import React, { useCallback, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  ListMusic,
  Minus,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Trash2
} from 'lucide-react';
import './ComboBuilderPrototype.css';

type PlayMode = 'segment' | 'combo';
type ComboEdge = 'left' | 'right';

interface ProtoSegment {
  id: string;
  name: string;
  startTime: number;
  endTime: number;
  color: string;
}

interface ProtoCombo {
  id: string;
  name: string;
  sourceSegmentIds: string[];
  color: string;
}

interface ComboRange {
  startIndex: number;
  endIndex: number;
  startTime: number;
  endTime: number;
  duration: number;
}

interface ComboModel {
  segments: ProtoSegment[];
  combos: ProtoCombo[];
  activeSegmentId: string;
  activeComboId: string | null;
  playMode: PlayMode;
  showMergeLane: boolean;
  activeSegment: ProtoSegment;
  activeCombo: ProtoCombo | null;
  comboRange: ComboRange | null;
  canAddLeft: boolean;
  canAddRight: boolean;
  canRemoveLeft: boolean;
  canRemoveRight: boolean;
  buildComboFromActiveSegment: () => void;
  addNeighborToActiveCombo: (edge: ComboEdge) => void;
  removeEdgeFromActiveCombo: (edge: ComboEdge) => void;
  selectSegment: (segmentId: string) => void;
  selectCombo: (comboId: string) => void;
  deleteActiveCombo: () => void;
  duplicateActiveCombo: () => void;
  toggleMergeLane: () => void;
  resetPrototype: () => void;
}

const SEGMENTS: ProtoSegment[] = [
  { id: 'seg-1', name: 'Intro groove', startTime: 0, endTime: 8, color: '#3b82f6' },
  { id: 'seg-2', name: 'Shoulder hits', startTime: 8, endTime: 17, color: '#f97316' },
  { id: 'seg-3', name: 'Turn prep', startTime: 17, endTime: 25, color: '#14b8a6' },
  { id: 'seg-4', name: 'Floor drop', startTime: 25, endTime: 36, color: '#ec4899' },
  { id: 'seg-5', name: 'Rise phrase', startTime: 36, endTime: 47, color: '#84cc16' },
  { id: 'seg-6', name: 'Chorus mark', startTime: 47, endTime: 59, color: '#a855f7' },
  { id: 'seg-7', name: 'Travel line', startTime: 59, endTime: 68, color: '#06b6d4' },
  { id: 'seg-8', name: 'Final pose', startTime: 68, endTime: 76, color: '#ef4444' }
];

const COMBO_COLORS = ['#469790', '#6C609F', '#C75B5B', '#3b82f6', '#a16207'];

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getSegmentIndex = (segments: ProtoSegment[], segmentId: string) =>
  segments.findIndex(segment => segment.id === segmentId);

const orderSourceIds = (segments: ProtoSegment[], sourceSegmentIds: string[]) =>
  Array.from(new Set(sourceSegmentIds))
    .sort((a, b) => getSegmentIndex(segments, a) - getSegmentIndex(segments, b));

const getComboRange = (segments: ProtoSegment[], combo: ProtoCombo | null): ComboRange | null => {
  if (!combo || combo.sourceSegmentIds.length === 0) return null;

  const indexes = combo.sourceSegmentIds
    .map(segmentId => getSegmentIndex(segments, segmentId))
    .filter(index => index >= 0)
    .sort((a, b) => a - b);

  if (indexes.length === 0) return null;

  const startIndex = indexes[0];
  const endIndex = indexes[indexes.length - 1];
  const startTime = segments[startIndex].startTime;
  const endTime = segments[endIndex].endTime;

  return {
    startIndex,
    endIndex,
    startTime,
    endTime,
    duration: endTime - startTime
  };
};

const getComboName = (segments: ProtoSegment[], sourceSegmentIds: string[]) => {
  const orderedIds = orderSourceIds(segments, sourceSegmentIds);
  const indexes = orderedIds
    .map(segmentId => getSegmentIndex(segments, segmentId))
    .filter(index => index >= 0);

  if (indexes.length === 0) return 'Combo';
  return `Combo ${indexes[0] + 1}-${indexes[indexes.length - 1] + 1}`;
};

const getTimelineStyle = (startTime: number, endTime: number, duration: number): React.CSSProperties => ({
  left: `${(startTime / duration) * 100}%`,
  width: `${((endTime - startTime) / duration) * 100}%`
});

const getPlaybackLabel = (model: ComboModel) => {
  if (model.playMode === 'combo' && model.activeCombo && model.comboRange) {
    return `${model.activeCombo.name} ${formatTime(model.comboRange.startTime)}-${formatTime(model.comboRange.endTime)}`;
  }

  return `${model.activeSegment.name} ${formatTime(model.activeSegment.startTime)}-${formatTime(model.activeSegment.endTime)}`;
};

const useComboPrototype = (): ComboModel => {
  const [combos, setCombos] = useState<ProtoCombo[]>([
    {
      id: 'combo-seed-1',
      name: 'Combo 2-4',
      sourceSegmentIds: ['seg-2', 'seg-3', 'seg-4'],
      color: COMBO_COLORS[0]
    },
    {
      id: 'combo-seed-2',
      name: 'Combo 5-6',
      sourceSegmentIds: ['seg-5', 'seg-6'],
      color: COMBO_COLORS[1]
    }
  ]);
  const [activeSegmentId, setActiveSegmentId] = useState('seg-4');
  const [activeComboId, setActiveComboId] = useState<string | null>('combo-seed-1');
  const [showMergeLane, setShowMergeLane] = useState(true);
  const [playMode, setPlayMode] = useState<PlayMode>('combo');

  const activeSegment = useMemo(
    () => SEGMENTS.find(segment => segment.id === activeSegmentId) ?? SEGMENTS[0],
    [activeSegmentId]
  );
  const activeCombo = useMemo(
    () => combos.find(combo => combo.id === activeComboId) ?? null,
    [activeComboId, combos]
  );
  const comboRange = useMemo(() => getComboRange(SEGMENTS, activeCombo), [activeCombo]);
  const canAddLeft = Boolean(comboRange && comboRange.startIndex > 0);
  const canAddRight = Boolean(comboRange && comboRange.endIndex < SEGMENTS.length - 1);
  const canRemoveLeft = Boolean(activeCombo && activeCombo.sourceSegmentIds.length > 1);
  const canRemoveRight = canRemoveLeft;

  const selectSegment = useCallback((segmentId: string) => {
    const selected = SEGMENTS.find(segment => segment.id === segmentId);
    if (!selected) return;
    setActiveSegmentId(segmentId);
    setPlayMode('segment');
  }, []);

  const selectCombo = useCallback((comboId: string) => {
    const combo = combos.find(candidate => candidate.id === comboId);
    if (!combo) return;
    const firstSourceSegment = combo.sourceSegmentIds[0];
    setActiveComboId(comboId);
    setActiveSegmentId(firstSourceSegment);
    setShowMergeLane(true);
    setPlayMode('combo');
  }, [combos]);

  const buildComboFromActiveSegment = useCallback(() => {
    const segment = SEGMENTS.find(candidate => candidate.id === activeSegmentId) ?? SEGMENTS[0];
    const sourceSegmentIds = [segment.id];
    const nextCombo: ProtoCombo = {
      id: `combo-${Date.now()}`,
      name: getComboName(SEGMENTS, sourceSegmentIds),
      sourceSegmentIds,
      color: COMBO_COLORS[combos.length % COMBO_COLORS.length]
    };

    setCombos(prev => [...prev, nextCombo]);
    setActiveComboId(nextCombo.id);
    setShowMergeLane(true);
    setPlayMode('combo');
  }, [activeSegmentId, combos.length]);

  const addNeighborToActiveCombo = useCallback((edge: ComboEdge) => {
    if (!activeCombo || !comboRange) return;

    const nextIndex = edge === 'left' ? comboRange.startIndex - 1 : comboRange.endIndex + 1;
    const nextSegment = SEGMENTS[nextIndex];
    if (!nextSegment) return;

    const nextIds = orderSourceIds(SEGMENTS, [...activeCombo.sourceSegmentIds, nextSegment.id]);
    const nextName = getComboName(SEGMENTS, nextIds);

    setCombos(prev => prev.map(combo =>
      combo.id === activeCombo.id
        ? { ...combo, sourceSegmentIds: nextIds, name: nextName }
        : combo
    ));
    setPlayMode('combo');
  }, [activeCombo, comboRange]);

  const removeEdgeFromActiveCombo = useCallback((edge: ComboEdge) => {
    if (!activeCombo || activeCombo.sourceSegmentIds.length <= 1) return;

    const orderedIds = orderSourceIds(SEGMENTS, activeCombo.sourceSegmentIds);
    const nextIds = edge === 'left' ? orderedIds.slice(1) : orderedIds.slice(0, -1);
    const nextName = getComboName(SEGMENTS, nextIds);

    setCombos(prev => prev.map(combo =>
      combo.id === activeCombo.id
        ? { ...combo, sourceSegmentIds: nextIds, name: nextName }
        : combo
    ));
    setActiveSegmentId(nextIds[0]);
    setPlayMode('combo');
  }, [activeCombo]);

  const deleteActiveCombo = useCallback(() => {
    if (!activeCombo) return;

    setCombos(prev => prev.filter(combo => combo.id !== activeCombo.id));
    setActiveComboId(null);
    setPlayMode('segment');
  }, [activeCombo]);

  const duplicateActiveCombo = useCallback(() => {
    if (!activeCombo) return;

    const duplicate: ProtoCombo = {
      ...activeCombo,
      id: `combo-${Date.now()}`,
      name: `${activeCombo.name} copy`,
      color: COMBO_COLORS[combos.length % COMBO_COLORS.length]
    };

    setCombos(prev => [...prev, duplicate]);
    setActiveComboId(duplicate.id);
    setShowMergeLane(true);
    setPlayMode('combo');
  }, [activeCombo, combos.length]);

  const toggleMergeLane = useCallback(() => {
    setShowMergeLane(prev => !prev);
  }, []);

  const resetPrototype = useCallback(() => {
    setCombos([
      {
        id: 'combo-seed-1',
        name: 'Combo 2-4',
        sourceSegmentIds: ['seg-2', 'seg-3', 'seg-4'],
        color: COMBO_COLORS[0]
      },
      {
        id: 'combo-seed-2',
        name: 'Combo 5-6',
        sourceSegmentIds: ['seg-5', 'seg-6'],
        color: COMBO_COLORS[1]
      }
    ]);
    setActiveSegmentId('seg-4');
    setActiveComboId('combo-seed-1');
    setShowMergeLane(true);
    setPlayMode('combo');
  }, []);

  return {
    segments: SEGMENTS,
    combos,
    activeSegmentId,
    activeComboId,
    playMode,
    showMergeLane,
    activeSegment,
    activeCombo,
    comboRange,
    canAddLeft,
    canAddRight,
    canRemoveLeft,
    canRemoveRight,
    buildComboFromActiveSegment,
    addNeighborToActiveCombo,
    removeEdgeFromActiveCombo,
    selectSegment,
    selectCombo,
    deleteActiveCombo,
    duplicateActiveCombo,
    toggleMergeLane,
    resetPrototype
  };
};

const VideoMock: React.FC<{ model: ComboModel }> = ({ model }) => (
  <section className="combo-video" aria-label="Video preview">
    <div className="combo-video-stage">
      <div className="combo-video-figure combo-video-figure-one" />
      <div className="combo-video-figure combo-video-figure-two" />
      <div className="combo-video-figure combo-video-figure-three" />
      <div className="combo-video-floor" />
      <div className="combo-video-badge">
        {model.playMode === 'combo' ? <ListMusic size={14} /> : <Play size={14} />}
        {model.playMode === 'combo' ? model.activeCombo?.name ?? 'Combo' : model.activeSegment.name}
      </div>
    </div>
  </section>
);

const TransportBar: React.FC<{ model: ComboModel }> = ({ model }) => (
  <div className="combo-transport">
    <button type="button" className="combo-icon-button" title="Previous segment">
      <ChevronLeft size={17} />
    </button>
    <button type="button" className="combo-play-button" title="Loop current selection">
      {model.playMode === 'combo' ? <Pause size={18} /> : <Play size={18} />}
    </button>
    <button type="button" className="combo-icon-button" title="Next segment">
      <ChevronRight size={17} />
    </button>
    <span className="combo-now-playing">{getPlaybackLabel(model)}</span>
  </div>
);

const ComboPanel: React.FC<{ model: ComboModel }> = ({ model }) => (
  <aside className="combo-panel">
    <div className="combo-panel-header">
      <div className="combo-panel-title">
        <ListMusic size={18} />
        <span>Combos</span>
      </div>
      <button
        type="button"
        className="combo-icon-button combo-icon-button-dark"
        onClick={model.buildComboFromActiveSegment}
        title="Build combo from current segment"
      >
        <Plus size={16} />
      </button>
    </div>

    <div className="combo-panel-list">
      {model.combos.map(combo => {
        const range = getComboRange(model.segments, combo);
        const isActive = model.activeComboId === combo.id;

        return (
          <button
            key={combo.id}
            type="button"
            className={`combo-panel-item ${isActive ? 'is-active' : ''}`}
            onClick={() => model.selectCombo(combo.id)}
            style={{ '--combo-color': combo.color } as React.CSSProperties}
          >
            <span className="combo-panel-stripe" />
            <span className="combo-panel-copy">
              <strong>{combo.name}</strong>
              <span>{range ? `${formatTime(range.startTime)}-${formatTime(range.endTime)}` : 'No parts'}</span>
            </span>
            <span className="combo-panel-count">{combo.sourceSegmentIds.length}</span>
          </button>
        );
      })}
    </div>

    <div className="combo-panel-actions">
      <button
        type="button"
        className="combo-panel-action"
        onClick={model.duplicateActiveCombo}
        disabled={!model.activeCombo}
        title="Duplicate active combo"
      >
        <Copy size={15} />
      </button>
      <button
        type="button"
        className="combo-panel-action"
        onClick={model.deleteActiveCombo}
        disabled={!model.activeCombo}
        title="Delete active combo"
      >
        <Trash2 size={15} />
      </button>
      <button
        type="button"
        className="combo-panel-action"
        onClick={model.resetPrototype}
        title="Reset prototype"
      >
        <RotateCcw size={15} />
      </button>
    </div>
  </aside>
);

const SegmentLane: React.FC<{ model: ComboModel }> = ({ model }) => {
  const duration = model.segments[model.segments.length - 1].endTime;

  return (
    <div className="combo-lane-shell">
      <div className="combo-lane-label">Segments</div>
      <div className="combo-lane combo-segment-lane" aria-label="Original segment lane">
        <div className="combo-lane-background" />
        <div className="combo-tick-layer">
          {[0, 1, 2, 3, 4].map(index => (
            <span key={index} style={{ left: `${index * 25}%` }} />
          ))}
        </div>
        {model.segments.map((segment, index) => {
          const isActive = model.activeSegmentId === segment.id && model.playMode === 'segment';
          const belongsToActiveCombo = Boolean(model.activeCombo?.sourceSegmentIds.includes(segment.id));

          return (
            <button
              key={segment.id}
              type="button"
              className={`combo-source-block ${isActive ? 'is-active' : ''} ${belongsToActiveCombo ? 'is-in-combo' : ''}`}
              style={{
                ...getTimelineStyle(segment.startTime, segment.endTime, duration),
                '--segment-color': segment.color
              } as React.CSSProperties}
              onClick={() => model.selectSegment(segment.id)}
              title={`Loop ${segment.name}`}
            >
              <span className="combo-block-index">{index + 1}</span>
              <span className="combo-block-name">{segment.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ComboPieceDividers: React.FC<{ model: ComboModel; combo: ProtoCombo }> = ({ model, combo }) => {
  const range = getComboRange(model.segments, combo);
  if (!range) return null;

  return (
    <>
      {combo.sourceSegmentIds.slice(1).map(segmentId => {
        const segment = model.segments.find(candidate => candidate.id === segmentId);
        if (!segment) return null;
        const left = ((segment.startTime - range.startTime) / range.duration) * 100;
        return <span key={segmentId} className="combo-divider" style={{ left: `${left}%` }} />;
      })}
    </>
  );
};

const EdgeControls: React.FC<{ model: ComboModel; edge: ComboEdge }> = ({ model, edge }) => {
  const isLeft = edge === 'left';

  return (
    <div className={`combo-edge-controls combo-edge-controls-${edge}`}>
      <button
        type="button"
        className="combo-edge-button combo-edge-add"
        onClick={(event) => {
          event.stopPropagation();
          model.addNeighborToActiveCombo(edge);
        }}
        disabled={isLeft ? !model.canAddLeft : !model.canAddRight}
        title={isLeft ? 'Add previous segment' : 'Add next segment'}
      >
        <Plus size={14} />
      </button>
      <button
        type="button"
        className="combo-edge-button combo-edge-remove"
        onClick={(event) => {
          event.stopPropagation();
          model.removeEdgeFromActiveCombo(edge);
        }}
        disabled={isLeft ? !model.canRemoveLeft : !model.canRemoveRight}
        title={isLeft ? 'Remove left segment' : 'Remove right segment'}
      >
        <Minus size={14} />
      </button>
    </div>
  );
};

const ActiveComboLane: React.FC<{ model: ComboModel }> = ({ model }) => {
  const duration = model.segments[model.segments.length - 1].endTime;
  if (!model.showMergeLane) return null;

  return (
    <div className="combo-lane-shell">
      <div className="combo-lane-label">Active combo</div>
      <div className="combo-lane combo-merge-lane" aria-label="Active combo lane">
        <div className="combo-lane-background" />
        <div className="combo-tick-layer">
          {[0, 1, 2, 3, 4].map(index => (
            <span key={index} style={{ left: `${index * 25}%` }} />
          ))}
        </div>
        {model.activeCombo && model.comboRange ? (
          <div
            role="button"
            tabIndex={0}
            className="combo-active-block"
            style={{
              ...getTimelineStyle(model.comboRange.startTime, model.comboRange.endTime, duration),
              '--combo-color': model.activeCombo.color
            } as React.CSSProperties}
            onClick={() => model.selectCombo(model.activeCombo?.id ?? '')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                model.selectCombo(model.activeCombo?.id ?? '');
              }
            }}
            title={`Loop ${model.activeCombo.name}`}
          >
            <EdgeControls model={model} edge="left" />
            <span className="combo-active-content">
              <span className="combo-active-name">{model.activeCombo.name}</span>
              <span className="combo-active-meta">{model.activeCombo.sourceSegmentIds.length} parts</span>
            </span>
            <ComboPieceDividers model={model} combo={model.activeCombo} />
            <EdgeControls model={model} edge="right" />
          </div>
        ) : (
          <button
            type="button"
            className="combo-empty-build"
            onClick={model.buildComboFromActiveSegment}
          >
            <Plus size={16} />
            Build combo
          </button>
        )}
      </div>
    </div>
  );
};

const TimelineControls: React.FC<{ model: ComboModel }> = ({ model }) => (
  <div className="combo-timeline-controls">
    <button
      type="button"
      className={`combo-toggle-button ${model.showMergeLane ? 'is-active' : ''}`}
      onClick={model.toggleMergeLane}
      title={model.showMergeLane ? 'Hide merge lane' : 'Show merge lane'}
    >
      {model.showMergeLane ? <EyeOff size={15} /> : <Eye size={15} />}
      Merge lane
    </button>
    <button
      type="button"
      className="combo-build-button"
      onClick={model.buildComboFromActiveSegment}
    >
      <Plus size={16} />
      Build combo
    </button>
  </div>
);

const ComboBuilderPrototype: React.FC = () => {
  const model = useComboPrototype();

  return (
    <main className="combo-prototype">
      <ComboPanel model={model} />
      <section className="combo-player-area">
        <VideoMock model={model} />
        <TransportBar model={model} />
        <section className="combo-timeline-area">
          <TimelineControls model={model} />
          <div className="combo-ruler">
            {['0:00', '0:15', '0:30', '0:45', '1:00', '1:15'].map(label => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <SegmentLane model={model} />
          <ActiveComboLane model={model} />
        </section>
      </section>
    </main>
  );
};

export default ComboBuilderPrototype;
